import { useState, useMemo } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useTumUtilityVendors } from '@/hooks/useTumUtilityVendors';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Plus, Search, Building2, Phone, Mail, Globe, Eye, EyeOff, 
  Edit2, Trash2, MoreVertical, Filter, Download, Upload, Users
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { VendorFormModal } from './components/VendorFormModal';
import { VendorCard } from './components/VendorCard';
import { VendorCustomerCredentialsModal } from './components/VendorCustomerCredentialsModal';
import type { TumUtilityVendor } from '@/types/tum';

const TumVendorManagement = () => {
  const { currentWorkspace } = useWorkspace();
  const { vendors, isLoading, createVendor, updateVendor, deleteVendor, isCreating, isUpdating, isDeleting } = useTumUtilityVendors();
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState<TumUtilityVendor | null>(null);
  const [deletingVendor, setDeletingVendor] = useState<TumUtilityVendor | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [credentialsVendor, setCredentialsVendor] = useState<TumUtilityVendor | null>(null);

  // Filter vendors
  const filteredVendors = useMemo(() => {
    return vendors.filter(vendor => {
      // Search filter
      const matchesSearch = !searchQuery || 
        vendor.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (vendor.vendor_code?.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && vendor.is_active !== false) ||
        (statusFilter === 'inactive' && vendor.is_active === false);
      
      return matchesSearch && matchesStatus;
    });
  }, [vendors, searchQuery, statusFilter]);

  // Handlers
  const handleAddVendor = () => {
    setEditingVendor(null);
    setShowFormModal(true);
  };

  const handleEditVendor = (vendor: TumUtilityVendor) => {
    setEditingVendor(vendor);
    setShowFormModal(true);
  };

  const handleDeleteVendor = (vendor: TumUtilityVendor) => {
    setDeletingVendor(vendor);
  };

  const handleManageCredentials = (vendor: TumUtilityVendor) => {
    setCredentialsVendor(vendor);
  };

  const confirmDeleteVendor = () => {
    if (deletingVendor) {
      deleteVendor(deletingVendor.id);
      setDeletingVendor(null);
    }
  };

  const handleSaveVendor = (vendorData: Partial<TumUtilityVendor>) => {
    if (editingVendor) {
      updateVendor({ id: editingVendor.id, ...vendorData });
    } else {
      createVendor(vendorData);
    }
    setShowFormModal(false);
    setEditingVendor(null);
  };

  // Stats
  const stats = useMemo(() => ({
    total: vendors.length,
    active: vendors.filter(v => v.is_active !== false).length,
    inactive: vendors.filter(v => v.is_active === false).length,
  }), [vendors]);

  return (
    <div className="p-4 space-y-4 max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">Vendor Management</h1>
          <p className="text-xs text-muted-foreground">Manage utility vendors for your workspace</p>
        </div>
        <Button size="sm" onClick={handleAddVendor} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          Add Vendor
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            <div>
              <p className="text-lg font-bold">{stats.total}</p>
              <p className="text-[10px] text-muted-foreground">Total Vendors</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-green-500" />
            <div>
              <p className="text-lg font-bold">{stats.active}</p>
              <p className="text-[10px] text-muted-foreground">Active</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-gray-400" />
            <div>
              <p className="text-lg font-bold">{stats.inactive}</p>
              <p className="text-[10px] text-muted-foreground">Inactive</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
            <SelectTrigger className="w-[140px] h-8 text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-1">
            <Button
              variant={viewMode === 'cards' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className="h-8 px-2"
            >
              Cards
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="h-8 px-2"
            >
              Table
            </Button>
          </div>
        </div>
      </Card>

      {/* Vendor List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : filteredVendors.length === 0 ? (
        <Card className="p-8 text-center">
          <Building2 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-medium text-foreground mb-1">No Vendors Found</h3>
          <p className="text-sm text-muted-foreground mb-3">
            {searchQuery || statusFilter !== 'all' 
              ? 'Try adjusting your filters' 
              : 'Get started by adding your first vendor'}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <Button size="sm" onClick={handleAddVendor}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add Vendor
            </Button>
          )}
        </Card>
      ) : viewMode === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredVendors.map(vendor => (
            <VendorCard
              key={vendor.id}
              vendor={vendor}
              onEdit={() => handleEditVendor(vendor)}
              onDelete={() => handleDeleteVendor(vendor)}
              onManageCredentials={() => handleManageCredentials(vendor)}
            />
          ))}
        </div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-2 font-medium">Vendor</th>
                  <th className="text-left p-2 font-medium">Code</th>
                  <th className="text-left p-2 font-medium">Contact</th>
                  <th className="text-left p-2 font-medium">Portal</th>
                  <th className="text-left p-2 font-medium">Status</th>
                  <th className="text-right p-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVendors.map(vendor => (
                  <tr key={vendor.id} className="border-b hover:bg-muted/30">
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={vendor.logo_url || ''} />
                          <AvatarFallback className="text-[10px] bg-primary/10">
                            {vendor.vendor_name.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{vendor.vendor_name}</span>
                      </div>
                    </td>
                    <td className="p-2 text-muted-foreground">
                      {vendor.vendor_code || '-'}
                    </td>
                    <td className="p-2 text-muted-foreground text-xs">
                      {vendor.customer_service_phone || vendor.customer_service_email || '-'}
                    </td>
                    <td className="p-2">
                      {vendor.portal_url ? (
                        <a 
                          href={vendor.portal_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline text-xs"
                        >
                          Open Portal
                        </a>
                      ) : '-'}
                    </td>
                    <td className="p-2">
                      <Badge variant={vendor.is_active !== false ? 'default' : 'secondary'} className="text-[10px]">
                        {vendor.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="p-2 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                            <MoreVertical className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditVendor(vendor)}>
                            <Edit2 className="h-3.5 w-3.5 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleManageCredentials(vendor)}>
                            <Users className="h-3.5 w-3.5 mr-2" />
                            Customer Credentials
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteVendor(vendor)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Vendor Form Modal */}
      <VendorFormModal
        open={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setEditingVendor(null);
        }}
        onSave={handleSaveVendor}
        vendor={editingVendor}
        isLoading={isCreating || isUpdating}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingVendor} onOpenChange={() => setDeletingVendor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vendor</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingVendor?.vendor_name}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteVendor}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Vendor Customer Credentials Modal */}
      {credentialsVendor && (
        <VendorCustomerCredentialsModal
          open={!!credentialsVendor}
          onClose={() => setCredentialsVendor(null)}
          vendor={credentialsVendor}
        />
      )}
    </div>
  );
};

export default TumVendorManagement;
